import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { isEmpty } from 'lodash';
import { Drawer, Grid, Button } from '@enterprise-ui/canvas-ui-react';
import FormAutocomplete from '../form/FormAutocomplete.js';
import FormCheckboxes from '../form/FormCheckboxes.js';
import FormCheckboxGroups from '../form/FormCheckboxGroups.js';
import FormDateTimePicker from '../form/FormDateTimePicker.js';
import FormMultiText from '../form/FormMultiText.js';
import FormSelect from '../form/FormSelect.js';
import FormTextField from '../form/FormTextField.js';
import FormRadio from '../form/FormRadio.js';
import SaveSearchAction from './SaveSearchAction.js';

var _excluded = ["type", "direction", "name", "label", "ariaLabel", "options", "multiselect"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var createFilter = function createFilter(filterMeta, formContext) {
  var type = filterMeta.type,
    direction = filterMeta.direction,
    name = filterMeta.name,
    label = filterMeta.label,
    ariaLabel = filterMeta.ariaLabel,
    options = filterMeta.options,
    multiselect = filterMeta.multiselect,
    restProps = _objectWithoutProperties(filterMeta, _excluded);
  switch (type) {
    case 'text':
      return /*#__PURE__*/React.createElement(FormTextField, _extends({
        formContext: formContext,
        name: name,
        label: label,
        "aria-label": ariaLabel
      }, restProps));
    case 'multi-text':
      return /*#__PURE__*/React.createElement(FormMultiText, _extends({
        formContext: formContext,
        name: name,
        label: label,
        "aria-label": ariaLabel
      }, restProps));
    case 'select':
      return /*#__PURE__*/React.createElement(FormSelect, _extends({
        formContext: formContext,
        name: name,
        label: label,
        "aria-label": ariaLabel,
        options: options
      }, restProps));
    case 'autocomplete':
      return /*#__PURE__*/React.createElement(FormAutocomplete, _extends({
        formContext: formContext,
        name: name,
        label: label,
        multiselect: multiselect,
        "aria-label": ariaLabel,
        options: options
      }, restProps));
    case 'date':
    case 'datetime-local':
      return /*#__PURE__*/React.createElement(FormDateTimePicker, _extends({
        formContext: formContext,
        type: type,
        name: name,
        label: label,
        "aria-label": ariaLabel,
        pinFieldInfo: true
      }, restProps));
    case 'checkboxes':
      return !isEmpty(options) && /*#__PURE__*/React.createElement(FormCheckboxes, _extends({
        formContext: formContext,
        name: name,
        label: label,
        "aria-label": ariaLabel,
        options: options
      }, restProps));
    case 'checkbox-groups':
      return !isEmpty(options) && /*#__PURE__*/React.createElement(FormCheckboxGroups, _extends({
        formContext: formContext,
        name: name,
        label: label,
        direction: direction,
        "aria-label": ariaLabel,
        options: options
      }, restProps));
    case 'radio':
      return /*#__PURE__*/React.createElement(FormRadio, _extends({
        formContext: formContext,
        name: name,
        label: label,
        direction: direction,
        options: options
      }, restProps));
    default:
      throw new Error('Unsupported Search Filter Type');
  }
};
var NovaFilterDrawer = function NovaFilterDrawer(_ref) {
  var isVisible = _ref.isVisible,
    onRequestClose = _ref.onRequestClose,
    onSubmit = _ref.onSubmit,
    headingText = _ref.headingText,
    filterMeta = _ref.filterMeta,
    filterActionsMeta = _ref.filterActionsMeta,
    values = _ref.values,
    defaultValues = _ref.defaultValues,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? 'right' : _ref$location;
  var formContext = useForm({
    mode: 'onTouched',
    defaultValues: defaultValues
  });
  var getValues = formContext.getValues,
    _formContext$formStat = formContext.formState,
    errors = _formContext$formStat.errors,
    isDirty = _formContext$formStat.isDirty,
    handleSubmit = formContext.handleSubmit,
    reset = formContext.reset,
    watch = formContext.watch;
  useEffect(function () {
    reset(_objectSpread({}, values));
  }, [reset, values]);
  var formValues = watch();

  // Need to create actions inside the component to access form context
  var createAction = function createAction(actionMeta) {
    switch (actionMeta.type) {
      case 'reset':
        return /*#__PURE__*/React.createElement(Button, {
          id: "reset",
          type: "secondary",
          fullWidth: true,
          onClick: function onClick() {
            reset(defaultValues);
          }
        }, "Reset");
      case 'submit':
        return /*#__PURE__*/React.createElement(Button, {
          id: "retrieve",
          fullWidth: true,
          type: "primary",
          onClick: handleSubmit(onSubmit)
        }, "Search");
      case 'save-search':
        return /*#__PURE__*/React.createElement(SaveSearchAction, {
          handleSaveSearch: actionMeta.action,
          filterValues: formValues,
          savedSearches: actionMeta.currentSavedSearches,
          noteToUser: actionMeta.noteToUser
        });
      default:
        // Standard canvas button types to create generic actions
        return /*#__PURE__*/React.createElement(Button, {
          fullWidth: true,
          id: actionMeta.id,
          type: actionMeta.type,
          onClick: function onClick() {
            return actionMeta.action(getValues());
          },
          disabled: actionMeta.disabled || !isDirty && isEmpty(errors)
        }, actionMeta.label);
    }
  };
  return /*#__PURE__*/React.createElement(Drawer, {
    isVisible: isVisible,
    location: location,
    className: "filter-drawer",
    headingText: headingText,
    onRequestClose: onRequestClose
  }, /*#__PURE__*/React.createElement("form", {
    onSubmit: function onSubmit(e) {
      return e.preventDefault();
    }
  }, /*#__PURE__*/React.createElement(Grid.Container, {
    className: "filter-drawer-body",
    direction: "column",
    spacing: "dense",
    noWrap: true
  }, Object.keys(filterMeta).map(function (key) {
    var filterRowMeta = filterMeta[key];
    return /*#__PURE__*/React.createElement(Grid.Item, null, /*#__PURE__*/React.createElement(Grid.Container, {
      direction: "row",
      spacing: "dense"
    }, filterRowMeta.map(function (filter) {
      return /*#__PURE__*/React.createElement(Grid.Item, {
        xs: filter.xs
      }, createFilter(filter, formContext));
    })));
  })), /*#__PURE__*/React.createElement("div", {
    className: "filter-drawer-footer"
  }, Object.keys(filterActionsMeta).map(function (key) {
    var filterActionsRowMeta = filterActionsMeta[key];
    return /*#__PURE__*/React.createElement(Grid.Container, {
      justify: "center",
      align: "center"
    }, filterActionsRowMeta.map(function (filterAction) {
      return /*#__PURE__*/React.createElement(Grid.Item, {
        xs: filterAction.xs
      }, createAction(filterAction));
    }));
  }))));
};

export { NovaFilterDrawer as default };
